/**
 * Cognito Hosted UI 連携（Authorization Code Grant + PKCE・フェーズ3）。
 *
 * 案A: マップ上の「ログイン」ボタン → 自前モーダル →「Google で続ける」で
 * Hosted UI（Google）へ遷移 → 認証後 redirect_uri に ?code=... で戻る →
 * code をトークンに交換して保持する。
 *
 * SPA のためクライアントシークレットは持たず PKCE で保護する。
 * 設定は Vite の環境変数（VITE_COGNITO_*）から読む（公開値・ClientId は秘密ではない）。
 */

const DOMAIN = import.meta.env.VITE_COGNITO_DOMAIN as string | undefined;
const CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID as string | undefined;
const REDIRECT_URI = import.meta.env.VITE_COGNITO_REDIRECT_URI as
  | string
  | undefined;

const STORAGE_TOKENS = 'reinvent_auth_tokens';
const STORAGE_PKCE = 'reinvent_pkce_verifier';

/** 認証機能が有効か（env が揃っているか）。 */
export function isAuthConfigured(): boolean {
  return !!(DOMAIN && CLIENT_ID && REDIRECT_URI);
}

export interface AuthTokens {
  id_token: string;
  access_token: string;
  /** 取得時刻 + expires_in（epoch ミリ秒）。 */
  expires_at: number;
}

export interface AuthUser {
  /** Cognito sub（アプリでは user_id = g_<sub> に採番）。 */
  sub: string;
  /** 表示名（Google プロフィール名）。 */
  name: string;
  email?: string;
  /** Google プロフィール画像 URL（あれば）。 */
  picture?: string;
}

// ---- PKCE ユーティリティ ----

function base64UrlEncode(bytes: Uint8Array): string {
  let str = '';
  for (const b of bytes) {
    str += String.fromCharCode(b);
  }
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function sha256(input: string): Promise<Uint8Array> {
  const data = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return new Uint8Array(digest);
}

function randomVerifier(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

// ---- ログイン開始 ----

/**
 * Hosted UI（Google）へ遷移してログインを開始する。
 * PKCE verifier を生成・保存し、challenge を付けて authorize へ飛ばす。
 */
export async function startLogin(): Promise<void> {
  if (!isAuthConfigured()) {
    return;
  }
  const verifier = randomVerifier();
  sessionStorage.setItem(STORAGE_PKCE, verifier);
  const challenge = base64UrlEncode(await sha256(verifier));

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: CLIENT_ID!,
    redirect_uri: REDIRECT_URI!,
    scope: 'openid email profile',
    identity_provider: 'Google', // Google に直行（Hosted UI のプロバイダ選択を省略）
    code_challenge: challenge,
    code_challenge_method: 'S256',
  });
  window.location.assign(`https://${DOMAIN}/oauth2/authorize?${params}`);
}

// ---- コールバック処理 ----

/**
 * URL に ?code=... があればトークンに交換して保持する。
 * 交換後は URL から code を取り除く。戻り値は取得できたトークン（無ければ null）。
 */
export async function handleRedirectCallback(): Promise<AuthTokens | null> {
  if (!isAuthConfigured()) {
    return null;
  }
  const url = new URL(window.location.href);
  const code = url.searchParams.get('code');
  if (!code) {
    return null;
  }
  const verifier = sessionStorage.getItem(STORAGE_PKCE);
  sessionStorage.removeItem(STORAGE_PKCE);
  if (!verifier) {
    return null;
  }

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: CLIENT_ID!,
    code,
    redirect_uri: REDIRECT_URI!,
    code_verifier: verifier,
  });

  try {
    const res = await fetch(`https://${DOMAIN}/oauth2/token`, {
      method: 'POST',
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
      body,
    });
    if (!res.ok) {
      console.error('token exchange failed', res.status);
      return null;
    }
    const json = (await res.json()) as {
      id_token: string;
      access_token: string;
      expires_in: number;
    };
    const tokens: AuthTokens = {
      id_token: json.id_token,
      access_token: json.access_token,
      // refresh_token は保存しない（トークン更新は未実装・XSS 時の被害を減らす・タスク55.6）
      expires_at: Date.now() + json.expires_in * 1000,
    };
    saveTokens(tokens);
    // URL から code / state を取り除く（戻り先はマップ）
    url.searchParams.delete('code');
    url.searchParams.delete('state');
    window.history.replaceState({}, '', url.toString());
    return tokens;
  } catch (err) {
    console.error('token exchange error', err);
    return null;
  }
}

// ---- トークン保持・ユーザー情報 ----

function saveTokens(tokens: AuthTokens): void {
  localStorage.setItem(STORAGE_TOKENS, JSON.stringify(tokens));
}

export function loadTokens(): AuthTokens | null {
  const raw = localStorage.getItem(STORAGE_TOKENS);
  if (!raw) {
    return null;
  }
  try {
    const tokens = JSON.parse(raw) as AuthTokens & { refresh_token?: string };
    if (tokens.expires_at <= Date.now()) {
      // 期限切れ（簡易版: リフレッシュは将来対応。今は破棄してログアウト扱い）
      localStorage.removeItem(STORAGE_TOKENS);
      return null;
    }
    // 以前の保存分に残っている refresh_token は消して保存し直す（タスク55.6）
    if (tokens.refresh_token !== undefined) {
      const { refresh_token: _drop, ...rest } = tokens;
      saveTokens(rest);
      return rest;
    }
    return tokens;
  } catch {
    return null;
  }
}

/** id_token（JWT）をデコードしてユーザー情報を取り出す。 */
export function decodeUser(tokens: AuthTokens): AuthUser | null {
  try {
    const payload = tokens.id_token.split('.')[1];
    const json = JSON.parse(
      atob(payload.replace(/-/g, '+').replace(/_/g, '/')),
    ) as {
      sub: string;
      name?: string;
      email?: string;
      picture?: string;
    };
    return {
      sub: json.sub,
      name: json.name ?? json.email ?? 'ユーザー',
      email: json.email,
      picture: json.picture,
    };
  } catch {
    return null;
  }
}

// ---- ログアウト ----

/** トークンを破棄し、Hosted UI のログアウトへ遷移する。 */
export function logout(): void {
  localStorage.removeItem(STORAGE_TOKENS);
  if (!isAuthConfigured()) {
    window.location.reload();
    return;
  }
  const params = new URLSearchParams({
    client_id: CLIENT_ID!,
    logout_uri: REDIRECT_URI!,
  });
  window.location.assign(`https://${DOMAIN}/logout?${params}`);
}
